import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { RFValue } from 'react-native-responsive-fontsize';
import { useObserver } from 'mobx-react-lite';
import { RouteComponentProps } from 'react-router-native';
import { isEmpty, isNull, isUndefined } from 'lodash';
import { Dropdown } from 'react-native-element-dropdown';
import ParentViewController from '../Shared/ParentViewController';
import ScreenTypes from '../../Domain/Types/ScreenTypes';
import { Colors } from '../../Config/Colors';
import Fonts from '../../Domain/Types/Fonts';
import { screenRepository } from '../../Domain/Repositories/ScreenRepository';
import useNavigation from '../../Domain/Hooks/useNavigation';
import DropdownModel from '../../Domain/Models/DropdownModel';
import CustomLeaderboardService from '../../Domain/Services/CustomLeaderboards/CustomLeaderboardService';
import CustomLeaderboardDTO from '../../Domain/Models/CustomLeaderboards/CustomLeaderboardDTO';
import IConfirmationDialogModel from '../../Domain/Models/IConfirmationDialogModel';
import ScreenService from '../../Domain/Services/ScreenService';
import { BaseModalType, modalController } from '../Shared/Modals/ModalController';
import INotificationModalData from '../../Domain/Models/INotificationModalData';

const MAXIMUM_ITEMS_PER_VIEW = 5;
const ITEM_HEIGHT = RFValue(25);
const MAXIMUM_HEIGHT = MAXIMUM_ITEMS_PER_VIEW * ITEM_HEIGHT;

interface ConfigureLeaderboardScreenProps
{
  screenId: string;
}

/**
 * Renders the ConfigureLeaderboardScreen
 */
export default function ConfigureLeaderboardScreen(props: RouteComponentProps<{},
  never, ConfigureLeaderboardScreenProps>): JSX.Element
{
  const { navigateToScreen } = useNavigation();
  const [loading, setLoading] = useState(false);
  const [screen, setScreen] = useState('');
  const [selectedLeaderboard, setSelectedLeaderboard] = useState<DropdownModel | null>(null);
  const [leaderboards, setLeaderboards] = useState<CustomLeaderboardDTO[]>([]);

  useEffect(() =>
  {
    getData();

    const { location } = props;
    if (isUndefined(location.state))
    {
      return;
    }

    const { screenId } = location.state;
    setScreen(screenId);
  }, []);

  const styles = StyleSheet.create({
    container: {
      width: '100%',
      height: '100%',
      justifyContent: 'center',
      alignItems: 'center',
    },
    contentContainer: {
      width: '95%',
      height: '100%',
      justifyContent: 'center',
      alignItems: 'center',
    },
    topBarContainer: {
      height: RFValue(40),
      width: '100%',
      justifyContent: 'center',
      alignItems: 'center',
    },
    topBarContentContainer: {
      height: '100%',
      width: '100%',
      justifyContent: 'center',
      alignItems: 'center',
    },
    activityIndicator: {
      width: '100%',
      height: '100%',
      justifyContent: 'center',
      alignItems: 'flex-start',
      paddingLeft: RFValue(6),
    },
    titleLabelContainer: {
      width: '100%',
      flex: 1,
      justifyContent: 'center',
      alignItems: 'flex-start',
    },
    titleContentContainer: {
      height: RFValue(13),
      width: '100%',
      justifyContent: 'center',
      alignItems: 'flex-start',
    },
    titleLabel: {
      fontSize: RFValue(10),
      fontFamily: Fonts.JudgeMedium,
      color: Colors.APP.teal,
      width: '90%',
      letterSpacing: RFValue(0.8),
      paddingLeft: RFValue(10),
    },
    primaryContainer: {
      flex: 1,
      width: '100%',
      justifyContent: 'center',
      alignItems: 'center',
    },
    primaryContentContainer: {
      height: '100%',
      width: '100%',
      justifyContent: 'flex-start',
      alignItems: 'center',
      backgroundColor: Colors.BLACK(0.5),
    },
    leaderboardSelectionContainer: {
      width: '100%',
      height: RFValue(40),
      justifyContent: 'center',
      alignItems: 'center',
    },
    leaderboardSelectionContentContainer: {
      width: '100%',
      height: '100%',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexDirection: 'row',
    },
    leaderboardTitleContainer: {
      height: '100%',
      justifyContent: 'center',
      alignItems: 'flex-start',
      paddingLeft: RFValue(11),
    },
    leaderboardTitleLabel: {
      fontSize: RFValue(10),
      fontFamily: Fonts.JudgeMedium,
      color: Colors.APP.teal,
      letterSpacing: RFValue(0.8),
    },
    leaderboardDropdownContainer: {
      height: '100%',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'row',
    },
    dropdownContainer: {
      height: '100%',
      justifyContent: 'center',
      alignItems: 'center',
      width: RFValue(150),
    },
    dropdownContentContainer: {
      height: '100%',
      width: '100%',
      padding: RFValue(6),
      justifyContent: 'center',
      alignItems: 'center',
    },
    dropdown: {
      backgroundColor: Colors.DROPDOWN.unselected,
      width: '100%',
      height: '100%',
      padding: RFValue(2),
      borderColor: Colors.DROPDOWN.border,
      borderWidth: RFValue(1),
    },
    dropdownPlaceholderText: {
      textAlign: 'center',
      fontSize: RFValue(10),
      fontFamily: Fonts.JudgeMedium,
      letterSpacing: RFValue(0.8),
      width: '70%',
      color: 'white',
    },
    dropdownSelectedText: {
      textAlign: 'center',
      fontSize: RFValue(10),
      fontFamily: Fonts.JudgeMedium,
      letterSpacing: RFValue(0.8),
      color: 'white',
      marginLeft: '5%',
    },
    dropdownListContainer: {
      backgroundColor: Colors.DROPDOWN.unselected,
      overflow: 'hidden',
      borderColor: Colors.DROPDOWN.border,
      borderWidth: RFValue(1),
    },
    dropdownListItemContainer: {
      height: ITEM_HEIGHT,
      justifyContent: 'center',
    },
    dropdownListItemText: {
      fontSize: RFValue(10),
      fontFamily: Fonts.JudgeMedium,
      textAlign: 'center',
      justifyContent: 'center',
      letterSpacing: RFValue(0.8),
      color: 'white',
    },
    dropdownArrowIcon: {
      width: RFValue(10),
      height: undefined,
      aspectRatio: 1,
      position: 'absolute',
      right: 0,
    },
    bottomBarContainer: {
      height: RFValue(40),
      width: '100%',
      justifyContent: 'center',
      alignItems: 'center',
    },
    buttonContainer: {
      flex: 1,
      width: '100%',
      justifyContent: 'flex-end',
      alignItems: 'flex-end',
      flexDirection: 'row',
    },
    buttonContentContainer: {
      height: '100%',
    },
    button: {
      height: '100%',
      justifyContent: 'center',
      alignItems: 'flex-start',
    },
    buttonLabelContainer: {
      height: RFValue(16),
      justifyContent: 'center',
      borderLeftWidth: RFValue(1),
      borderLeftColor: Colors.APP.teal,
    },
    buttonLabel: {
      fontSize: RFValue(10),
      fontFamily: Fonts.JudgeMedium,
      color: 'white',
      letterSpacing: RFValue(0.8),
      paddingLeft: RFValue(6),
      paddingRight: RFValue(10),
    },
  });

  const valid = useMemo(() =>
  {
    if (loading || isEmpty(screen) || isNull(selectedLeaderboard))
    {
      return false;
    }

    return true;
  }, [screen, loading, selectedLeaderboard]);

  const defaultValid = useMemo(() =>
  {
    if (loading || isEmpty(screen))
    {
      return false;
    }

    return true;
  }, [screen, loading]);

  const getTitleContent = (): JSX.Element =>
  {
    if (loading)
    {
      return (
        <View style={styles.activityIndicator}>
          <ActivityIndicator color={Colors.APP.teal} size="large" />
        </View>
      );
    }

    if (isEmpty(screen))
    {
      return (
        <View style={[styles.titleContentContainer, {
          opacity: 0.5,
        }]}
        >
          <Text style={styles.titleLabel}>
            No screen selected
          </Text>
        </View>
      );
    }

    const repoScreen = screenRepository.getById(screen);
    const title = repoScreen?.name ?? '';

    return (
      <View style={styles.titleContentContainer}>
        <Text style={styles.titleLabel}>
          {title}
        </Text>
      </View>
    );
  };

  const onBackButtonPressed = (): void =>
  {
    const repoScreen = screenRepository.getById(screen);
    if (isUndefined(repoScreen))
    {
      navigateToScreen(ScreenTypes.VenueAreas, {
        areaId: '',
      });
      return;
    }

    navigateToScreen(ScreenTypes.VenueAreas, {
      areaId: repoScreen.areaId,
    });
  };

  const dropdownItems = useMemo(() => leaderboards.map((x) => ({
    label: x.name,
    value: x.id,
  })), [leaderboards]);

  const onChange = (item: DropdownModel): void =>
  {
    setSelectedLeaderboard(item);
  };

  const getData = (): void =>
  {
    (async (): Promise<void> =>
    {
      setSelectedLeaderboard(null);
      setLoading(true);
      const response = await CustomLeaderboardService.get();
      setLoading(false);

      if (!response.success || isUndefined(response.data))
      {
        setLeaderboards([]);
        return;
      }

      setLeaderboards(response.data);
    })();
  };

  const renderLeaderboardSelectionContainer = useMemo(() => (
    <View style={styles.leaderboardSelectionContentContainer}>
      <View style={styles.leaderboardTitleContainer}>
        <Text style={styles.leaderboardTitleLabel}>
          Display Leaderboard
        </Text>
      </View>

      <View style={styles.leaderboardDropdownContainer}>
        <View style={styles.dropdownContainer}>
          <View style={styles.dropdownContentContainer}>
            <Dropdown
              placeholder="Select Leaderboard"
              style={styles.dropdown}
              placeholderStyle={styles.dropdownPlaceholderText}
              selectedTextStyle={styles.dropdownSelectedText}
              containerStyle={styles.dropdownListContainer}
              itemContainerStyle={styles.dropdownListItemContainer}
              itemTextStyle={styles.dropdownListItemText}
              data={dropdownItems}
              maxHeight={MAXIMUM_HEIGHT}
              labelField="label"
              valueField="value"
              value={selectedLeaderboard}
              activeColor={Colors.DROPDOWN.selected}
              iconStyle={styles.dropdownArrowIcon}
              iconColor={Colors.DROPDOWN.icon}
              onChange={onChange}
            />
          </View>
        </View>

        <View style={styles.buttonContentContainer}>
          <TouchableOpacity
            style={styles.button}
            onPress={getData}
          >
            <View style={styles.buttonLabelContainer}>
              <Text style={styles.buttonLabel}>
                Refresh
              </Text>
            </View>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  ), [valid, dropdownItems]);

  const setScreenLeaderboard = useCallback(() =>
  {
    if (isNull(selectedLeaderboard) || isUndefined(screen))
    {
      showFailedModal('An unexpected error occured.\n\nPlease refresh the page and try again.');
      return;
    }

    const notification: IConfirmationDialogModel = {
      title: 'Info',
      message: 'Are you sure you want to update the leaderboard for this screen?',
      negativeAction: (): void => modalController.hide(),
      positiveAction: async (): Promise<void> =>
      {
        const getScreenResponse = await ScreenService.get(screen);
        const getScreenResponseData = getScreenResponse.data;

        if (!getScreenResponse.success || isUndefined(getScreenResponseData))
        {
          showFailedModal(getScreenResponse.reason);
          return;
        }

        const {
          id, areaId, partyId, channelConfig,
        } = getScreenResponseData;

        const response = await ScreenService.updateScreen({
          id,
          areaId,
          channelConfig,
          partyId,
          leaderboardId: selectedLeaderboard.value,
        });

        if (response.success)
        {
          showSuccessModal('Successfully set screen leaderboard!', (): void => modalController.hide());
          return;
        }

        showFailedModal(response.reason);
      },
    };

    modalController.show({
      modalType: BaseModalType.GenericConfirmation,
      data: notification,
    });
  }, [screen, selectedLeaderboard]);

  const setDefaultLeaderboard = useCallback(() =>
  {
    if (isUndefined(screen))
    {
      showFailedModal('An unexpected error occured.\n\nPlease refresh the page and try again.');
      return;
    }

    const notification: IConfirmationDialogModel = {
      title: 'Info',
      message: 'Are you sure you want to set the default leaderboard for this screen?',
      negativeAction: (): void => modalController.hide(),
      positiveAction: async (): Promise<void> =>
      {
        setSelectedLeaderboard(null);

        const getScreenResponse = await ScreenService.get(screen);
        const getScreenResponseData = getScreenResponse.data;

        if (!getScreenResponse.success || isUndefined(getScreenResponseData))
        {
          showFailedModal(getScreenResponse.reason);
          return;
        }

        const {
          id, areaId, partyId, channelConfig,
        } = getScreenResponseData;

        const response = await ScreenService.updateScreen({
          id,
          areaId,
          channelConfig,
          partyId,
          leaderboardId: null,
        });

        if (response.success)
        {
          showSuccessModal('Successfully set screen leaderboard to default!', (): void => modalController.hide());
          return;
        }

        showFailedModal(response.reason);
      },
    };

    modalController.show({
      modalType: BaseModalType.GenericConfirmation,
      data: notification,
    });
  }, [screen, selectedLeaderboard]);

  const showSuccessModal = (
    message: string,
    onConfirmed = (): void => modalController.showPrevious(),
  ): void =>
  {
    const notification: INotificationModalData = {
      title: 'Info',
      message,
      disableCloseOnConfirm: true,
      onConfirmButtonPressed: onConfirmed,
    };

    modalController.show({
      modalType: BaseModalType.SimpleNotification,
      data: notification,
    });
  };

  const showFailedModal = (reason: string): void =>
  {
    const notification: INotificationModalData = {
      title: 'Error!',
      message: reason,
      disableCloseOnConfirm: true,
      onConfirmButtonPressed: (): void =>
      {
        modalController.showPrevious();
      },
    };

    modalController.show({
      modalType: BaseModalType.SimpleNotification,
      data: notification,
    });
  };

  return useObserver(() => (
    <ParentViewController
      screenType={ScreenTypes.ConfigureLeaderboard}
      onSyncProgressChanged={setLoading}
      leftButtonAction={onBackButtonPressed}
    >
      <View style={styles.container}>
        <View style={styles.contentContainer}>
          <View style={styles.topBarContainer}>
            <View style={styles.topBarContentContainer}>
              <View style={styles.titleLabelContainer}>
                {getTitleContent()}
              </View>
            </View>
          </View>

          <View style={styles.primaryContainer}>
            <View style={styles.primaryContentContainer}>
              <View style={styles.leaderboardSelectionContainer}>
                {renderLeaderboardSelectionContainer}
              </View>
            </View>
          </View>

          <View style={styles.bottomBarContainer}>
            <View style={styles.buttonContainer}>
              <View style={styles.buttonContentContainer}>
                <TouchableOpacity
                  style={[styles.button, {
                    opacity: defaultValid ? 1 : 0.5,
                  }]}
                  disabled={!defaultValid}
                  onPress={setDefaultLeaderboard}
                >
                  <View style={styles.buttonLabelContainer}>
                    <Text style={styles.buttonLabel}>
                      Set to Default Leaderboard
                    </Text>
                  </View>
                </TouchableOpacity>
              </View>

              <View style={styles.buttonContentContainer}>
                <TouchableOpacity
                  style={[styles.button, {
                    opacity: valid ? 1 : 0.5,
                  }]}
                  disabled={!valid}
                  onPress={setScreenLeaderboard}
                >
                  <View style={styles.buttonLabelContainer}>
                    <Text style={styles.buttonLabel}>
                      Set Screen Leaderboard
                    </Text>
                  </View>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </View>
    </ParentViewController>
  ));
}
